import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserX, AlertTriangle, Archive, Ban, Trash2, Mail, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchBar from '@/components/shared/SearchBar';
import { API, toast } from '@/App';
import { getAuthToken } from '@/utils/authSession';

const InactiveUsersManagement = () => {
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [monthsFilter, setMonthsFilter] = useState(6);
  const [searchQuery, setSearchQuery] = useState('');
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionType, setActionType] = useState('');
  const [deactivationReason, setDeactivationReason] = useState('');

  useEffect(() => {
    loadInactiveUsers();
  }, [roleFilter, monthsFilter]);

  const loadInactiveUsers = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const params = new URLSearchParams();
      if (roleFilter !== 'all') params.append('role', roleFilter);
      params.append('months', monthsFilter);

      const response = await axios.get(`${API}/admin/users/inactive?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInactiveUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs inactifs');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setShowActionDialog(true);
  };

  const confirmAction = async () => {
    if (!selectedUser) return;

    try {
      const token = getAuthToken();
      
      switch (actionType) {
        case 'archive':
          await axios.patch(`${API}/admin/users/${selectedUser.id}/archive`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Utilisateur archivé avec succès');
          break;
          
        case 'deactivate':
          if (!deactivationReason) {
            toast.error('Veuillez fournir une raison');
            return;
          }
          await axios.patch(`${API}/admin/users/${selectedUser.id}/deactivate`, 
            { reason: deactivationReason },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Utilisateur désactivé avec succès');
          break;
          
        case 'delete':
          await axios.delete(`${API}/admin/users/${selectedUser.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Utilisateur supprimé avec succès');
          break;
          
        case 'contact':
          // TODO: Implement email sending
          toast.info(`Email de rappel envoyé à ${selectedUser.email}`);
          break;
      }
      
      setShowActionDialog(false);
      setDeactivationReason('');
      loadInactiveUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'action');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      vendeur: 'bg-blue-100 text-blue-700',
      livreur: 'bg-purple-100 text-purple-700',
      agent_pr: 'bg-cyan-100 text-cyan-700',
      admin: 'bg-red-100 text-red-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  const filteredUsers = inactiveUsers.filter(user =>
    user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Utilisateurs Inactifs</h2>
          <p className="text-gray-600 mt-1">
            Gérez les comptes inactifs depuis plus de {monthsFilter} mois
          </p>
        </div>
        <Button onClick={loadInactiveUsers} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Alert Banner */}
      {filteredUsers.length > 0 && (
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">
                {filteredUsers.length} utilisateur(s) inactif(s) détecté(s)
              </p>
              <p className="text-sm text-amber-800 mt-1">
                Ces comptes n'ont pas été utilisés depuis plus de {monthsFilter} mois. 
                Vous pouvez les contacter, archiver ou supprimer.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Rechercher</Label>
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Nom ou email..."
              />
            </div>
            <div>
              <Label>Rôle</Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  <SelectItem value="vendeur">Vendeurs</SelectItem>
                  <SelectItem value="livreur">Livreurs</SelectItem>
                  <SelectItem value="agent_pr">Agents PR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Inactivité</Label>
              <Select value={monthsFilter.toString()} onValueChange={(v) => setMonthsFilter(parseInt(v))}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 mois</SelectItem>
                  <SelectItem value="6">6 mois</SelectItem>
                  <SelectItem value="12">12 mois</SelectItem>
                  <SelectItem value="24">24 mois</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des utilisateurs inactifs ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <UserX className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun utilisateur inactif trouvé</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Dernière activité</TableHead>
                  <TableHead>Inactivité</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{user.full_name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getRoleBadgeColor(user.role)}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {user.last_login 
                        ? new Date(user.last_login).toLocaleDateString('fr-FR')
                        : new Date(user.created_at).toLocaleDateString('fr-FR')
                      }
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-orange-100 text-orange-700">
                        {user.inactive_months} mois
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAction(user, 'contact')}
                          title="Envoyer un email"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAction(user, 'archive')}
                          title="Archiver"
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAction(user, 'deactivate')}
                          title="Désactiver"
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleAction(user, 'delete')}
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'contact' && 'Contacter l\'utilisateur'}
              {actionType === 'archive' && 'Archiver l\'utilisateur'}
              {actionType === 'deactivate' && 'Désactiver l\'utilisateur'}
              {actionType === 'delete' && 'Supprimer l\'utilisateur'}
            </DialogTitle>
            <DialogDescription>
              {selectedUser && (
                <>
                  Action sur: <strong>{selectedUser.full_name}</strong> ({selectedUser.email})
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {actionType === 'contact' && (
              <p className="text-sm text-gray-600">
                Un email de rappel sera envoyé à cet utilisateur pour l'inviter à se reconnecter.
              </p>
            )}
            
            {actionType === 'archive' && (
              <p className="text-sm text-gray-600">
                L'utilisateur sera archivé mais ses données seront conservées. 
                Il pourra être restauré ultérieurement.
              </p>
            )}
            
            {actionType === 'deactivate' && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  L'utilisateur ne pourra plus se connecter jusqu'à réactivation par un admin.
                </p>
                <div>
                  <Label>Raison de la désactivation *</Label>
                  <Input
                    value={deactivationReason}
                    onChange={(e) => setDeactivationReason(e.target.value)}
                    placeholder="Ex: Inactivité prolongée"
                  />
                </div>
              </div>
            )}
            
            {actionType === 'delete' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded">
                <p className="text-sm font-semibold text-red-900 mb-2">
                  ⚠️ Action irréversible
                </p>
                <p className="text-sm text-red-700">
                  Toutes les données de l'utilisateur seront définitivement supprimées. 
                  Cette action ne peut pas être annulée.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={confirmAction}
              className={actionType === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InactiveUsersManagement;
